/**
 * 各府県の情報 (故障ページに定義されてある情報)
 */
export type PrefInfo = {
  id: number;
  name?: string /* 府県名 */;
  flg?: PrefBreakDownCode /* 故障状態 */;
};

/**
 * 各府県の故障状態(故障ページに定義されてあるコード)
 */
type PrefBreakDownCode =
  | 0 /* 故障なし*/
  | 1 /* 一部サービス故障/故障の疑い */
  | 2 /* 全サービス故障 */
  | 3 /* 故障お問い合わせ多数 */;

/**
 * エリア別故障ステータス(画面表示時に使用)
 */
export type AreaBreakDownStatus =
  | /* 故障都道府県なし */ "none"
  | /* 一部の都道府県で故障あり */ "partial"
  | /* 全サービス故障 */ "all";

/**
 * 西日本のエリア毎の故障状態
 */
export type WestArea<T> = {
  hokuriku: {
    ishikawa: T;
    toyama: T;
    fukui: T;
  };
  tokai: {
    gifu: T;
    aichi: T;
    shizuoka: T;
    mie: T;
  };
  kinki: {
    shiga: T;
    kyoto: T;
    osaka: T;
    hyogo: T;
    nara: T;
    wakayama: T;
  };
  cyugoku: {
    tottori: T;
    shimane: T;
    okayama: T;
    hiroshima: T;
    yamaguchi: T;
  };
  shikoku: {
    tokushima: T;
    kagawa: T;
    ehime: T;
    kochi: T;
  };
  kyusyu: {
    fukuoka: T;
    saga: T;
    nagasaki: T;
    kumamoto: T;
    oita: T;
    miyazaki: T;
    kagoshima: T;
    okinawa: T;
  };
};

type FetchCallback = (doc: Document) => void;

/**
 * fetch先のURLを決定する
 *
 * 本番の場合、"https://u-see-map.ntt-west.co.jp/" が適用されるが、
 * このページを加工する事はできないので、URLに特定のパラメータを付与する事で
 * fetch先を変更する事ができるようにしている
 *
 * - URL中に`_kz_fetch_dist=devX`が指定された場合、"https://localhost:8080/dummy/u-see-mapX/"
 * - URL中に`_kz_fetch_dist=lolX`が指定された場合、"https://main-kaizenplatform.ssl-lolipop.jp/dummy_ntt-west/top/u-see-mapX/"
 *
 * (※ "X"は0〜9までの任意の数字)
 *
 * @returns fetch先URL
 */
export const selectFetchDistination = (): string => {
  const d = new URL(location.href).searchParams.get("_kz_fetch_dist") || "";
  let url;
  if (d.match(/^dev/)) {
    // ローカル
    url = `https://localhost:8080/dummy/u-see-map${d.substring(3)}/`;
  } else if (d.match(/^lol/)) {
    // ロリポップ
    url = `https://main-kaizenplatform.ssl-lolipop.jp/dummy_ntt-west/top/u-see-map${d.substring(3)}/`;
  } else {
    // 本番
    url = "https://u-see-map.ntt-west.co.jp/";
  }
  return url;
};

/**
 * 指定されたURLのfetchを行う
 *
 * @param url fetch先
 * @param fn コールバック関数
 */
export const fetchURL = (url: string, fn: FetchCallback): void => {
  fetch(url, {
    method: "GET",
  })
    .then((res) => res.text())
    .then(async (text) => {
      // コールバック関数の呼び出し
      //   const $doc = $($.parseHTML(text)); // このやり方だとなぜか$(#prefs)のhiddenの値が取得できない
      const doc = new DOMParser().parseFromString(text, "text/html");
      fn(doc);
    });
};

export const scrapeArea = (prefs: PrefInfo[]): WestArea<PrefInfo> => {
  const west: WestArea<PrefInfo> = {
    hokuriku: {
      ishikawa: { id: 17 },
      toyama: { id: 16 },
      fukui: { id: 18 },
    },
    tokai: {
      gifu: { id: 21 },
      shizuoka: { id: 22 },
      aichi: { id: 23 },
      mie: { id: 24 },
    },
    kinki: {
      shiga: { id: 25 },
      kyoto: { id: 26 },
      osaka: { id: 27 },
      hyogo: { id: 28 },
      nara: { id: 29 },
      wakayama: { id: 30 },
    },
    cyugoku: {
      tottori: { id: 31 },
      shimane: { id: 32 },
      okayama: { id: 33 },
      hiroshima: { id: 34 },
      yamaguchi: { id: 35 },
    },
    shikoku: {
      tokushima: { id: 36 },
      kagawa: { id: 37 },
      ehime: { id: 38 },
      kochi: { id: 39 },
    },
    kyusyu: {
      fukuoka: { id: 40 },
      saga: { id: 41 },
      nagasaki: { id: 42 },
      kumamoto: { id: 43 },
      oita: { id: 44 },
      miyazaki: { id: 45 },
      kagoshima: { id: 46 },
      okinawa: { id: 47 },
    },
  };

  prefs.forEach((json: PrefInfo) => {
    Object.keys(west).forEach((kArea) => {
      const area = west[kArea];
      Object.keys(area).forEach((kPref) => {
        const pref = area[kPref];
        if (pref.id === json.id) {
          pref.name = json.name;
          pref.flg = json.flg;
        }
      });
    });
  });
  return west;
};

/**
 * 指定されたareaの故障状況の集計した結果を返す
 *
 * @param area 集計対象エリア
 * @returns "all" | "partial" | "none"
 */
export const summaryArea = (area: Record<string, PrefInfo>): AreaBreakDownStatus => {
  const keys = Object.keys(area);

  // 全てが「全サービス故障」の場合
  const hasAll = keys.every((k) => {
    return area[k].flg === 2;
  });
  if (hasAll) return "all";

  // 「故障なし」or「故障お問い合わせ多数」のみの場合
  const isNone = keys.every((k) => {
    return area[k].flg === 0 || area[k].flg === 3;
  });
  if (isNone) return "none";

  // それ以外は "partial"
  return "partial";
};
